import { useEffect, useCallback } from 'react';
import { BrowserRouter, Routes, Route, useNavigate } from 'react-router-dom';
import RootLayout from './components/RootLayout';
import {
    ChartDetailPage,
    CreateChartPage,
    CreateDashboardPage,
    CreateFormPage,
    CreateReportPage,
    CreateTabGroupPage,
    DashboardDetailPage,
    EditDashboardPage,
    EditChartPage,
    EditReportPage,
    EditFormPage,
    EditTabGroupPage,
    FoldersPage,
    SubfoldersPage,
    FormDetailPage,
    FormFullscreenPage,
    FormStepsPage,
    GoogleCallbackPage,
    LoginPage,
    PlaygroundPage,
    ReportDetailPage,
    RootPage,
    SettingsMfaPage,
    SettingsPage,
    SettingsPasswordPage,
    TabGroupDetailPage
} from './pages';
import { GoogleAnalytics } from './components/common/GoogleAnalytics';
import { MantineProvider } from '@mantine/core';
import { Notifications } from '@mantine/notifications';
import { createColorSchemeManager } from '@bi-studio/shared';
import { theme } from './theme';
import '@mantine/core/styles.css';
import '@mantine/notifications/styles.css';

const colorSchemeManager = createColorSchemeManager('mantine-color-scheme');

import { useAuth } from "@bi-studio/shared/hooks";

function SessionHandler() {
    const navigate = useNavigate();
    const { logout } = useAuth();

    const handleSessionExpired = useCallback(() => {
        // Clear auth state immediately to prevent infinite loops
        logout();

        Notifications.show({
            id: 'session-expired',
            title: 'Session Expired',
            message: 'Your session has expired because you logged in from another device or browser.',
            color: 'red',
            autoClose: 5000,
            withCloseButton: false,
        });

        navigate('/login');
    }, [navigate, logout]);

    useEffect(() => {
        window.addEventListener('session-expired', handleSessionExpired);
        return () => {
            window.removeEventListener('session-expired', handleSessionExpired);
        };
    }, [handleSessionExpired]);

    return null;
}

import * as Sentry from '@sentry/react';

const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);

function App() {
    return (
        <MantineProvider theme={theme} colorSchemeManager={colorSchemeManager}>
            <GoogleAnalytics />
            <Notifications />
            <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
                    <SessionHandler />
                    <RootLayout>
                        <SentryRoutes>
                            <Route path="/" element={<RootPage />} />
                            <Route path="/login" element={<LoginPage />} />
                            <Route path="/auth/google/callback" element={<GoogleCallbackPage />} />
                            <Route path="/folders" element={<FoldersPage />} />
                            <Route path="/folders/:folderId" element={<SubfoldersPage />} />
                            <Route path="/dashboards/:id" element={<DashboardDetailPage />} />
                            <Route path="/reports/:id" element={<ReportDetailPage />} />
                            <Route path="/charts/:id" element={<ChartDetailPage />} />
                            <Route path="/charts/:id/edit" element={<EditChartPage />} />
                            <Route path="/create/chart" element={<CreateChartPage />} />
                            <Route path="/create/dashboard" element={<CreateDashboardPage />} />
                            <Route path="/create/report" element={<CreateReportPage />} />
                            <Route path="/reports/:id/edit" element={<EditReportPage />} />
                            <Route path="/create/form" element={<CreateFormPage />} />
                            <Route path="/forms/:id" element={<FormDetailPage />} />
                            <Route path="/forms/:id/edit" element={<EditFormPage />} />
                            <Route path="/forms/:id/fullscreen" element={<FormFullscreenPage />} />
                            <Route path="/forms/:id/steps" element={<FormStepsPage />} />
                            <Route path="/settings" element={<SettingsPage />} />
                            <Route path="/settings/mfa" element={<SettingsMfaPage />} />
                            <Route path="/settings/password" element={<SettingsPasswordPage />} />
                            <Route path="/dashboards/:id/edit" element={<EditDashboardPage />} />
                            <Route path="/create/tab-group" element={<CreateTabGroupPage />} />
                            <Route path="/tab-groups/:id/edit" element={<EditTabGroupPage />} />
                            <Route path="/tab-groups/:id" element={<TabGroupDetailPage />} />
                            <Route path="/playground" element={<PlaygroundPage />} />
                        </SentryRoutes>
                </RootLayout>
            </BrowserRouter>
        </MantineProvider>
    );
}

export default App;
